import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkStepper from "../../components/atoms/TkStepper";
import TkBackButton from "../../components/molecules/TkBackButton";
import {
  FormClassRetire,
  IllnessAndDeathRetireFlowReason,
  NormalRetireFlowReason,
  Prefectures,
  RetireFlowReason,
  RetireReason,
  TransferReason,
} from "../../assets/data/Constants";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import { TkGetCode, TkGetCodeList } from "../../utils/TkGetCode";
import { IAPI_STA003_Response, IAPI_STA008_Request } from "../../type/apiType";
import { businessTypeJudgement, isLessThanOneYear } from "./hooks/useTools";
import {
  contextEditInfoList,
  fakeTableList,
  retireSchema,
  schema,
  stepper,
} from "./parts";
import { httpAPI_BUS001, httpAPI_STA003, httpAPI_STA008 } from "./api/request";
import "./_style.scss";
import { API_STA008_BuildReqParams } from "../../utils/buildRequestParams";
import _ from "lodash";
import { IJob_32Props } from "../../type/contextType/businessContext/IJob_32";
import TkPropertyMessageGet from "../../utils/TkPropertyMessageGet";

/**
 * SCRC060120 退職情報　登録
 * @return SCRC060120
 */
const SCRC060120 = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const isKiko =
    state?.authInfo?.userType === authorityUserType.userTypeNumber.KIKO;
  const contractorNo = isKiko
    ? businessState.common?.contractorNo
    : state.authInfo?.contractorNo;
  const context = businessState.job_32;
  const [open, setOpen] = useState(false);
  const [statecorpInfoList, setStatecorpInfoList] = useState<any>([]);
  const [retireReasonCd, setRetireReasonCd] = useState("");
  const [schemaData, setSchemaData] = useState(schema);
  const telValue = useRef("");
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));
  const [openWarning, setOpenWarning] = useState(false);
  const inputDataRef = useRef<any>();
  const buttonIndexRef = useRef<any>();

  const handleRefer = async (
    applyId: number
  ): Promise<IAPI_STA003_Response> => {
    return httpAPI_STA003({
      contractorNo,
      staffNo: location.state?.staffNo ?? context?.staffNo,
      applyId,
    });
  };

  const handleInit = async () => {
    let data: any = context ?? {};
    if (!location.state?.backButtonFlag) {
      // 退職者情報詳細参照API 呼び出し
      try {
        const res = await handleRefer(
          location.state?.applyId ??
            context?.applyId ??
            businessState?.agentRequest?.applyId
        );

        if (res.resultCd === "I0000") {
          data = res;
          (data as IJob_32Props).tempSaveStep = res.temporarySaveStep;
          setState({ ...state, errorMsgId: "" });
        } else {
          setState({ ...state, errorMsgId: res.resultCd });
          return;
        }
      } catch {
        navigate("/error");
      }
    }

    let table = stateFakeTableList.current;

    // 初期表示
    for (let i in data) {
      let targetObj = table.find((item) => item.keyword === i)!;

      targetObj && (targetObj.setDefaultValue = data[i as keyof typeof data]);
      targetObj &&
        targetObj.type === "text" &&
        (targetObj.text = data[i as keyof typeof data]);
    }

    let contractorName = table.find(
      (item) => item.keyword === "contractorName"
    )!;
    contractorName.showLine = isKiko;
    contractorName.contractorNumber = `${businessState.common?.contractorNo}:${businessState.common?.contractorName}`;

    // 退職者名
    let staffNameKanji = table.find(
      (item) => item.keyword === "staffNameKanji"
    )!;
    staffNameKanji.setDefaultValue = data.staffNameKanji;

    // 退職年月日
    let retireDate = table.find((item) => item.keyword === "retireDate")!;
    // 異動理由="6"（適用外異動減）配置換え減かつ、異動後施設番号=999の場合
    if (
      data.transferReason === TransferReason["適用外異動減"] &&
      data.aFacilityNo === "999"
    ) {
      retireDate.disabled = true; // 変更不可
      retireDate.value = data.transferDate; // 異動年月日
    }

    if (data.retireDateModifiableFlg === "0") {
      retireDate.disabled = true; // 非活性
    }

    // 退職理由
    table.find((item) => item.keyword === "retireReasonCd")!.setDefaultValue =
      data.nRetireReasonCd ?? data.fRetireReasonCd;
    setRetireReasonCd(data.nRetireReasonCd ?? data.fRetireReasonCd ?? "");

    // 退職者住所
    let resObj = {
      prefectures: TkGetCode(Prefectures, data.prefectureCd),
      city: data.addressCity ?? "",
      address: data.addressOthers ?? "",
    };
    table.find((item) => item.keyword === "address")!.setDefaultValue = resObj; // デフォルトを設定

    let retireReasonCd = table.find(
      (item) => item.keyword === "retireReasonCd"
    )!;

    let zipcode = table.find((item) => item.keyword === "zipcode")!;
    let tel = table.find((item) => item.keyword === "tel")!;

    // 退職理由
    retireReasonCd.onGetValue = (e: any) => {
      setRetireReasonCd(e);
    };

    // 住所検索
    zipcode.getPostalCodeValue = (value: any) => {
      const data = value.value;

      let resObj = {
        prefectures: data.address1,
        city: `${data.address2}${data.address3}`,
        address: "",
      };

      table.find((item) => item.keyword === "address")!.valueObject = resObj;
      setState({
        ...state,
        errorMsgId: "",
      });
    };

    zipcode.getPostalCodeError = (value: any) => {
      setState({
        ...state,
        errorMsgId: value,
      });
    };

    tel.onGetInputValue = (e: any) => {
      telValue.current = e?.target?.valueForShow;
    };

    setStatecorpInfoList([...table]);

    setBusinessState({
      ...businessState,
      job_32: {
        ...context,
        ...data,
        retirementRequestOrder: data.retirementInvoiceOrder,
      },
    });
    setState({
      ...state,
      errorMsgId: "",
    });
  };

  useEffect(() => {
    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let table = stateFakeTableList.current;
    let retireProcedureReason = table.find(
      (item) => item.keyword === "retireProcedureReason"
    )!;
    let retireReason = table.find((item) => item.keyword === "retireReasonCd")!;

    retireProcedureReason.showLine =
      retireReasonCd !== RetireReason["犯罪等による退職"];

    retireReason.isGuideMessage =
      retireReasonCd === RetireReason["業務上の傷病又は業務上の死亡による退職"];

    if (retireReasonCd === RetireReason["普通退職"]) {
      retireProcedureReason.radioPropsList = TkGetCodeList(
        NormalRetireFlowReason
      );
    } else if (
      retireReasonCd === RetireReason["業務上の傷病又は業務上の死亡による退職"]
    ) {
      retireProcedureReason.radioPropsList = TkGetCodeList(
        IllnessAndDeathRetireFlowReason
      );
    }

    if (retireReasonCd !== RetireReason["犯罪等による退職"]) {
      setSchemaData({ ...schema, ...retireSchema });
    } else {
      setSchemaData(schema);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retireReasonCd]);

  const makeHttpObj = (
    obj: any,
    context: IJob_32Props,
    businessType: string
  ): IAPI_STA008_Request => {
    const reqObj: IAPI_STA008_Request = {
      contractorNo, // 共済契約者番号
      applyId: location.state?.applyId ?? context?.applyId, // 申請id
      tempSaveStep: 1, // 一時保存ステップ
      businessType, // 業務タイプ
      staffNo: context?.staffNo, // 法人職員番号
      staffNameKanji: obj.staffNameKanji, // 法人職員氏名（漢字）
      staffNameKana: obj.staffNameKana, // 法人職員氏名（ｶﾅ）
      BNameKanji: context?.bNameKanji, // 変更前氏名（漢字）
      BNameKana: context?.bNameKana, // 変更前氏名（ｶﾅ）
      birthday: obj.birthday, // 法人職員生年月日
      sex: context?.sex, // 法人職員性別
      jobCd: context?.jobCd, // 職種番号
      retireReasonCd: obj.retireReasonCd, // 退職理由コード
      retireProcedureReason:
        retireReasonCd !== RetireReason["犯罪等による退職"]
          ? obj.retireProcedureReason
          : null, // 退職手続きの理由
      prefectureRetirementJoinFlg: context?.prefectureRetirementJoinFlg, // 都道府県の退職金制度への加入有無
      retirementRequestOrderFlg: context?.retirementRequestOrder, // 退職金請求の順番
      firstJoinDate: context?.firstJoinDate, // 初回加入年月日
      changedName: context?.changedName, // 変更後氏名（漢字）
      changedNameKana: context?.changedNameKana, // 変更後氏名ｶﾅ
      retireDate: obj.retireDate, // 退職年月日
      zipcode: obj.zipcode, // 退職者住所（郵便番号）
      prefectureCd:
        Prefectures[obj.address.prefectures as keyof typeof Prefectures], // 退職者住所（都道府県）
      addressCity: obj.address.city, // 退職者住所（市区町村）
      addressOthers: obj.address.address, // 退職者住所（その他）
      tel: telValue.current, // 退職者電話番号
      year: context?.year, // 年度
      apr: context?.apr, // 4月
      may: context?.may, // 5月
      jun: context?.jun, // 6月
      jul: context?.jul, // 7月
      aug: context?.aug, // 8月
      sep: context?.sep, // 9月
      oct: context?.oct, // 10月
      nov: context?.nov, // 11月
      dec: context?.dec, // 12月
      jan: context?.jan, // 1月
      feb: context?.feb, // 2月
      mar: context?.mar, // 3月
      sixMonthsSalaryBeforeApplyList: context?.sixMonthsSalaryBeforeApplyList, // 申請前6ヶ月間の本俸月額リスト
      sixMonthsSalaryBeforeApplyDetailList:
        context?.sixMonthsSalaryBeforeApplyDetailList, // 申請前6か月本俸月額の調整額明細リスト
      formProspectNum: context?.withholdingTaxSlipTotal, // 源泉徴収票登録枚数
      page1Kbn: context?.page1Kbn, // 1枚目源泉徴収票・特別徴収票の区分
      page1PaymentAmt: context?.page1PaymentAmt, // 1枚目の支払金額
      page1WithholdingAmt: context?.page1WithholdingAmt, // 1枚目の源泉徴収税額
      page1ScAmtCity: context?.page1ScAmtCity, // 1枚目の特別徴収税額（市町村民税）
      page1ScAmtPrefecture: context?.page1ScAmtPrefecture, // 1枚目の特別徴収税額（都道府県民税）
      page1LengthOfService: context?.page1LengthOfService, // 1枚目の勤続年数
      page1EmployedDate: context?.page1EmployedDate, // 1枚目の就職年月日
      page1RetiredDate: context?.page1RetiredDate, // 1枚目の退職年月日
      page1DeductionPeriodYear: context?.page1DeductionPeriodYear, // 1枚目の控除期間（年）
      page1DeductionPeriodMonth: context?.page1DeductionPeriodMonth, // 1枚目の控除期間（月）
      page2Kbn: context?.page2Kbn, // 2枚目源泉徴収票・特別徴収票の区分
      page2PaymentAmt: context?.page2PaymentAmt, // 2枚目の支払金額
      page2WithholdingAmt: context?.page2WithholdingAmt, // 2枚目の源泉徴収税額
      page2ScAmtCity: context?.page2ScAmtCity, // 2枚目の特別徴収税額（市町村民税）
      page2ScAmtPrefecture: context?.page2ScAmtPrefecture, // 2枚目の特別徴収税額（都道府県民税）
      page2LengthOfService: context?.page2LengthOfService, // 2枚目の勤続年数
      page2EmployedDate: context?.page2EmployedDate, // 2枚目の就職年月日
      page2RetiredDate: context?.page2RetiredDate, // 2枚目の退職年月日
      page2DeductionPeriodYear: context?.page2DeductionPeriodYear, // 2枚目の控除期間（年）
      page2DeductionPeriodMonth: context?.page2DeductionPeriodMonth, // 2枚目の控除期間（月）
      page3Kbn: context?.page3Kbn, // 3枚目源泉徴収票・特別徴収票の区分
      page3PaymentAmt: context?.page3PaymentAmt, // 3枚目の支払金額
      page3WithholdingAmt: context?.page3WithholdingAmt, // 3枚目の源泉徴収税額
      page3ScAmtCity: context?.page3ScAmtCity, // 3枚目の特別徴収税額（市町村税民）
      page3ScAmtPrefecture: context?.page3ScAmtPrefecture, // 3枚目の特別徴収税額（都道府県民税）
      page3LengthOfService: context?.page3LengthOfService, // 3枚目の勤続年数
      page3EmployedDate: context?.page3EmployedDate, // 3枚目の就職年月日
      page3RetiredDate: context?.page3RetiredDate, // 3枚目の退職年月日
      page3DeductionPeriodYear: context?.page3DeductionPeriodYear, // 3枚目の控除期間（年）
      page3DeductionPeriodMonth: context?.page3DeductionPeriodMonth, // 3枚目の控除期間（月）
      formList: context?.formList, // 帳票情報リスト
      transferDate: context?.transferDate, // 異動年月日
      continueTransferApplicable1Flg: context?.continueTransferApplicable1Flg, // 継続異動該当条件１フラグ
      continueTransferApplicable2Flg: context?.continueTransferApplicable2Flg, // 継続異動該当条件2フラグ
      updateDateTime: context?.updateDateTime, // 更新日時
      screenId: "SCRC060120", // 画面ID
    };

    return API_STA008_BuildReqParams(reqObj);
  };

  const makeContextObj = (
    data: any,
    businessType: any
  ): IAPI_STA008_Request => {
    let contextData: IJob_32Props = {
      ...context,
      staffNameKanji: data.staffNameKanji, // 法人職員氏名（漢字）
      staffNameKana: data.staffNameKana, // 法人職員氏名（ｶﾅ）
      birthday: data.birthday, // 法人職員生年月日
      retireDate: data.retireDate, // 退職年月日
      nRetireReasonCd: data.retireReasonCd, // 退職理由コード（届出）
      retireProcedureReason:
        data.retireReasonCd !== RetireReason["犯罪等による退職"]
          ? data.retireProcedureReason
          : null, // 退職手続きの理由
      zipcode: data.zipcode, // 退職者住所（郵便番号）
      prefectureCd:
        Prefectures[data.address.prefectures as keyof typeof Prefectures], // 退職者住所（都道府県）
      addressCity: data.address.city, // 退職者住所（市区町村）
      addressOthers: data.address.address, // 退職者住所（その他）
      tel: telValue.current, // 退職者電話番号
    };

    if (
      !context?.bNameKanji &&
      context?.staffNameKanji?.split("　")[0] !== data.staffNameKanji
    ) {
      // ①持回情報.変更前氏名（漢字）が設定されていない かつ 持回情報.法人職員氏名（漢字）≠ 入出力項目.退職者名 の場合
      contextData.bNameKanji = context?.staffNameKanji;
    } else if (
      // ②持回情報.変更前氏名（漢字）が設定されている かつ 持回情報.法人職員氏名（ｶﾅ）≠ 入出力項目.退職者名カナ の場合
      context?.bNameKanji &&
      context?.staffNameKanji === data.staffNameKanji
    ) {
      delete contextData.bNameKana;
    }

    if (!context?.bNameKana && context?.staffNameKana !== data.staffNameKana) {
      // ①持回情報.変更前氏名（ｶﾅ）が設定されていない かつ 持回情報.法人職員氏名（ｶﾅ）≠ 入出力項目.退職者名カナ の場合
      contextData.bNameKana = context?.staffNameKana;
    } else if (
      context?.bNameKana &&
      context?.staffNameKana === data.staffNameKana
    ) {
      delete contextData.bNameKana;
    }

    contextEditInfoList[businessType].forEach((item) => {
      delete contextData[item];
    });
    if (businessType === "JOB-33") {
      contextData.formList =
        context?.formList?.filter(
          (item) =>
            !(
              item.formClass ===
                FormClassRetire["退職所得の源泉徴収票_退職届1枚目"] ||
              item.formClass ===
                FormClassRetire["退職所得の源泉徴収票_退職届2枚目"] ||
              item.formClass ===
                FormClassRetire["退職所得の源泉徴収票_退職届3枚目"]
            )
        ) ?? [];
    }

    return contextData;
  };

  const onCheck = (data: any, index: any) => {
    inputDataRef.current = data;
    buttonIndexRef.current = index;
    if (index === 1) {
      // 業務チェック
      let errorMsgId = "";
      if (new Date(data.retireDate) < new Date(context?.joinDate!)) {
        errorMsgId = "TKMSG_SCR0016E";
      } else if (new Date(data.birthday) > new Date(context?.joinDate!)) {
        errorMsgId = "TKMSG_SCR0122E";
      }
      setState({
        ...state,
        errorMsgId,
      });
      if (errorMsgId) return;

      const today = new Date();
      const nextYearDate = new Date(
        today.setFullYear(today.getFullYear() + 1)
      ).setHours(0, 0, 0, 0);
      const retireDate = new Date(data.retireDate).setHours(0, 0, 0, 0);
      if (retireDate >= nextYearDate) {
        //入出力項目.退職年月日 ≧ システム日付 + 1年
        setOpenWarning(true);
      } else {
        handleSubmit(data, index);
      }
    } else if (index === 0) {
      handleSubmit(data, index);
    }
  };

  const handleSubmit = async (data: any, index: any) => {
    const businessType = businessTypeJudgement(
      data.retireReasonCd,
      data.retireProcedureReason,
      context,
      data.retireDate
    );
    let contextData = makeContextObj(data, businessType);

    try {
      if (index === 1) {
        // 退職日の整合性チェックAPI 呼び出し
        const checkRes = await httpAPI_BUS001({
          checkedFlg: "0",
          contractorNo,
          facilityNo: context?.facilityNo,
          retireDate: data.retireDate,
          retireDateModifiableFlg: context?.retireDateModifiableFlg,
          staffNo: context?.staffNo,
        });

        if (checkRes.resultCd !== "I0000") {
          setState({
            ...state,
            errorMsgId: checkRes.resultCd,
          });
          return;
        }
      }

      //退職者情報詳細更新API 呼び出し
      const updateRes = await httpAPI_STA008({
        ...makeHttpObj(data, contextData, businessType),
      });

      if (updateRes.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });

        if (index === 0) {
          setBusinessState({
            ...businessState,
            job_32: {
              ...contextData,
              ...updateRes,
            },
          });
          setOpen(true);
          return;
        }

        // 退職者情報詳細参照API 呼び出し
        const referRes = await handleRefer(updateRes.applyId!);

        if (referRes.resultCd === "I0000") {
          setState({
            ...state,
            errorMsgId: "",
          });
          setBusinessState({
            ...businessState,
            common: {
              ...businessState.common,
              businessType,
            },
            job_32: {
              ...contextData,
              ...referRes,
            },
          });

          if (
            data.retireReasonCd === RetireReason["普通退職"] &&
            data.retireProcedureReason ===
              RetireFlowReason["法人（業務上の）都合"] &&
            isLessThanOneYear(context?.firstJoinDate, data.retireDate)
          ) {
            navigate("/SCRC060127");
          } else {
            navigate("/SCRC060121");
          }
        } else {
          setState({
            ...state,
            errorMsgId: referRes.resultCd,
          });
        }
      } else {
        setState({
          ...state,
          errorMsgId: updateRes.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const onClickWarnigOk = () => {
    setOpenWarning(false);
    handleSubmit(inputDataRef.current, buttonIndexRef.current);
  };

  return (
    <Box data-testid="SCRC060120" className="SCRC060120">
      <TkStepper
        steps={stepper}
        activeStep={0}
        savaStep={context?.tempSaveStep}
      />

      <TkFakeTable
        list={statecorpInfoList}
        ruleSchema={schemaData}
        onCheckAndSubmit={onCheck}
        buttonGroup={<TkBackButton />}
        submitButtonGroup={[
          { submitButtonText: "一時保存" },
          { submitButtonText: "次へ" },
        ]}
      />

      <TkMessageDialog
        open={open}
        onOky={() => setOpen(false)}
        title="一時保存完了"
        contextText="　一時保存が完了しました。"
        okText="はい"
        cancelText=""
      />
      <TkMessageDialog
        open={openWarning}
        onOky={onClickWarnigOk}
        onCancel={() => setOpenWarning(false)}
        onClose={() => setOpenWarning(false)}
        title="警告"
        contextText={TkPropertyMessageGet("TKMSG_SCR0029W")}
        okText="確認"
        cancelText="戻る"
      />
    </Box>
  );
};

export default SCRC060120;
